import { type BackendExperiment, type ExperimentLayer } from '../../../types';
import { getLayerValues } from '../backend';

import type {
	DynamicExperiment,
	DynamicExperimentLayer,
	DynamicExperimentLayerDefinitionWithLayerType,
	DynamicExperimentRequest,
	DynamicExperiments,
	EntityFilters,
	ProductEdition,
} from './types';

const WILDCARD = '*';

export const mapDynamicToBackendExperiment = ({
	dynamicExperiments,
	productEdition,
}: {
	dynamicExperiments: DynamicExperiments;
	productEdition?: ProductEdition;
}): BackendExperiment => {
	const experimentLayers = dynamicExperiments
		.filter(isSameConfluenceTier(productEdition))
		.map((dynamicExp): ExperimentLayer => getLayerValues(dynamicExp.statsigLayerId));

	return {
		experimentId: experimentLayers.map((layer) => layer.layerId).join('_'),
		shadowExperimentId: experimentLayers.map((layer) => layer.shadowId).join('_'),
		experimentLayers,
	};
};

export const mapDynamicExperiments = ({
	dynamicExperiments,
	requestedEntities,
	productEdition,
	isHello,
}: {
	dynamicExperiments: DynamicExperiments;
	requestedEntities: EntityFilters;
	productEdition?: ProductEdition;
	isHello?: boolean;
}): DynamicExperimentRequest => {
	const experiments = dynamicExperiments
		.filter(isSameConfluenceTier(productEdition))
		.filter(isHelloTenant(isHello))
		.filter(containsRequestedEntities(requestedEntities))
		.filter(containsRequestedSubEntities(requestedEntities))
		.map((dynamicExp): DynamicExperimentLayerDefinitionWithLayerType => {
			const { layerId, shadowId } = getLayerValues(dynamicExp.statsigLayerId);
			return {
				definition: {
					layerId,
					shadowId,
					entity: dynamicExp.entity,
					subEntity: dynamicExp.subEntity,
				},
				layerType: dynamicExp.layerType,
			};
		});

	const groupedExperimentLayers = experiments.reduce<DynamicExperimentLayer[]>(
		(experimentLayers, definitionWithLayerType) => {
			const existingLayer = experimentLayers.find(
				(layer) => layer.name === definitionWithLayerType.layerType,
			);
			if (!existingLayer) {
				experimentLayers.push({
					name: definitionWithLayerType.layerType,
					definitions: [definitionWithLayerType.definition],
					layerId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
					shadowId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
				});
				return experimentLayers;
			}

			existingLayer.definitions?.push(definitionWithLayerType.definition);
			return experimentLayers;
		},
		[],
	);

	return {
		experimentLayers: groupedExperimentLayers,
	};
};

const isSameConfluenceTier =
	(productEdition?: ProductEdition) => (dynamicExp: DynamicExperiment) => {
		// Opt for specificity. Does not filter out values that do not have the confluenceTier attribute
		if (!dynamicExp.confluenceTier || dynamicExp.confluenceTier.length === 0) {
			return true;
		}
		return dynamicExp.confluenceTier?.find(
			(experimentTier) => productEdition?.['confluence'] === experimentTier,
		);
	};

const isHelloTenant = (isHello?: boolean) => (dynamicExp: DynamicExperiment) => {
	if (dynamicExp.hello === undefined || dynamicExp.hello === null) {
		return true;
	}

	return dynamicExp.hello === isHello;
};

const containsRequestedEntities =
	(entityFilter: EntityFilters) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.entity || dynamicExp.entity === WILDCARD) {
			return true;
		}

		const isFilteredEntity = entityFilter.find(
			(filteredEntity) => dynamicExp.entity === filteredEntity.entity,
		);
		return isFilteredEntity ?? false;
	};

const containsRequestedSubEntities =
	(entityFilter: EntityFilters) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.subEntity || dynamicExp.subEntity === WILDCARD) {
			return true;
		}

		const isFilteredSubEntity = entityFilter.find(
			(filteredEntity) => dynamicExp.subEntity === filteredEntity.subEntity,
		);
		return isFilteredSubEntity ?? false;
	};
